/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Grid, Link, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useScript } from '../../helpers';
import {
  AddressField,
  getMissingAddressFields,
  hasAnyAddressValue,
  isBlank,
} from '../../utils/address';
import { CountryOption, getCountryLabel, getCountryOptions } from '../../utils/countries';
import { Address, EMPTY_ADDRESS, parseGooglePlace } from '../../utils/googlePlace';

/**
 * Saisie de l'adresse D'UNE PERSONNE, partagée par les points d'entrée
 * d'inscription et par le profil.
 *
 * DEUX états sont rendus, toutes les transitions étant déclenchées
 * explicitement :
 *   search — champ Google + lien « mon adresse n'est pas trouvée »
 *   manual — les 4 champs éditables + retour à la recherche
 *
 * Toute adresse exploitable mène en `manual`, remplie : sélection Google,
 * adresse déjà connue du parent au montage, ou adresse qui arrive plus tard
 * (page profil). L'utilisateur constate ce qui a réellement été retenu —
 * Google omet souvent le numéro de rue, et une adresse sans code postal est
 * normale hors de France (STU-28) — directement dans des champs corrigeables.
 *
 * Un récapitulatif en lecture seule a existé entre les deux : retiré. Il
 * ajoutait un clic et une étape pour montrer ce que les quatre champs remplis
 * montrent déjà, en mieux puisqu'ils sont corrigeables, et il flottait sans
 * libellé ni cadre au milieu d'un formulaire de champs arrondis.
 *
 * Une seule sortie de `manual` : le lien « Revenir à la recherche », qui vide
 * l'adresse. Surtout PAS de sortie dérivée de l'état de l'adresse : `filled`
 * change à chaque frappe, et l'utilisateur qui efface son dernier caractère se
 * retrouverait renvoyé au champ de recherche en pleine saisie.
 *
 * L'ancienne bascule automatique au blur a été retirée : rien n'indiquait à
 * l'utilisateur que le mode manuel existait (STU-28).
 *
 * Sert AUSSI à l'adresse d'un bien assuré (Habitation) — mais alors avec
 * `limitFrenchCountry`, parce que Studcorp n'assure que des logements situés
 * en France. La distinction n'est pas « quel composant », elle est « quelle
 * adresse » : celle d'une PERSONNE est ouverte au monde entier depuis STU-28,
 * celle d'un LOGEMENT reste française.
 */

type Mode = 'search' | 'manual';

interface AddressInputProps {
  value: Address | null;
  onChange: (address: Address) => void;
  disabled?: boolean;
  limitFrenchCountry?: boolean;
  /** « Il y a lieu de signaler une erreur maintenant » — typiquement : le
   *  champ a été touché et l'adresse reste incomplète. Le parent dit QUAND
   *  signaler ; c'est le composant qui décide QUELS champs peindre en rouge,
   *  via `getMissingAddressFields` (voir plus bas). */
  error?: boolean;
}

const google_api_key = process.env.REACT_APP_GOOGLE_API_KEY;

/**
 * Nombre de caractères tapés à partir duquel on peut affirmer qu'aucune
 * suggestion n'a été retenue — et non que l'utilisateur commence à peine.
 *
 * En dessous, Google n'a souvent pas encore de liste à proposer : le message
 * serait du bruit affiché à quelqu'un en pleine frappe, sur toutes les adresses
 * du monde, pas seulement sur celles qui posent problème. Huit caractères, soit
 * l'ordre de grandeur d'un numéro plus le début d'un nom de rue : à ce stade la
 * liste est là (le message dit d'y choisir) ou elle ne vient pas (le message
 * indique la sortie, juste en dessous).
 */
const SEARCH_GUIDANCE_MIN_LENGTH = 8;

/**
 * Ce résultat est-il assez consistant pour être enregistré ?
 *
 * Sert de garde-fou sur ce que renvoie Google : une place inexploitable ne
 * doit pas écraser ce que le parent avait déjà.
 *
 * `hasAnyAddressValue` est trop permissif ici : sous `limitFrenchCountry`,
 * `parseGooglePlace` force `country = 'FR'` dès qu'il y a des composants, si
 * bien qu'une place sans aucun type utile produit `{ '', '', '', 'FR' }` — une
 * adresse réduite à « France », rue et ville muettes.
 *
 * On exige donc une rue OU une ville. Surtout PAS de code postal : beaucoup de
 * pays n'en ont pas (STU-28), et une adresse camerounaise avec rue, ville et
 * pays doit passer.
 *
 * Question DISTINCTE de `hasAnyAddressValue`, qui décide, elle, s'il y a
 * quelque chose à montrer à l'utilisateur : une fiche héritée réduite au pays
 * ne mérite pas d'être enregistrée depuis Google, mais elle doit rester sous
 * les yeux de son propriétaire. Les deux prédicats mènent au même état
 * (`manual`), ils ne peuvent donc pas diverger sur l'affichage.
 */
const isUsableAddress = (address: Address): boolean =>
  !isBlank(address.street) || !isBlank(address.city);

/** Morceaux affichables d'une ligne d'adresse : un champ fait uniquement
 *  d'espaces ne compte pas (`isBlank`, la règle du projet), sinon une ville
 *  faite d'espaces produit « 75002   , France ». */
const compact = (values: string[]): string[] =>
  values.filter((v) => !isBlank(v)).map((v) => v.trim());

/**
 * Ce que le champ de recherche affiche quand une adresse est DÉJÀ enregistrée.
 * Purement visuel — aucune règle de complétude ici, c'est `utils/address` qui
 * en décide — mais nécessaire : un champ de recherche vide devant une adresse
 * enregistrée ment autant qu'un champ rempli devant rien.
 */
const toSearchText = (address: Address, language: string): string =>
  compact([
    address.street,
    compact([address.postalCode, address.city]).join(' '),
    getCountryLabel(address.country, language),
  ]).join(', ');

/** Deux instances peuvent cohabiter sur un même écran : les `id` des champs
 *  doivent rester uniques pour que le `for` des labels vise le bon contrôle. */
let instanceCounter = 0;

const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  disabled = false,
  limitFrenchCountry = false,
  error = false,
}) => {
  const { t, i18n } = useTranslation('common');

  const address = value ?? EMPTY_ADDRESS;
  // Le parent a-t-il quelque chose dans cette adresse ? C'est la seule
  // question qui décide de ce qu'on affiche : des champs déjà saisis ne
  // doivent jamais disparaître des yeux de l'utilisateur.
  const filled = hasAnyAddressValue(address);

  // Le parent dit seulement qu'il y a lieu de signaler une erreur ; les champs
  // à peindre en rouge sont ceux que la règle PARTAGÉE du projet juge
  // manquants. Propager `error` tel quel aux quatre champs peignait « Code
  // postal » en rouge pour une adresse camerounaise — alors qu'il n'y est pas
  // requis et que le message affiché juste en dessous dit le contraire
  // (STU-28) — et faisait rougir d'un bloc les trois autres champs dès la
  // première lettre saisie.
  const missingFields: AddressField[] = error
    ? getMissingAddressFields(address)
    : [];
  const fieldError = (field: AddressField): boolean =>
    missingFields.includes(field);

  const [idPrefix] = useState(() => `address-${(instanceCounter += 1)}`);

  // Une adresse déjà renseignée ouvre directement les champs, remplis ; sinon
  // la recherche.
  const [mode, setMode] = useState<Mode>(() => (filled ? 'manual' : 'search'));

  // Ce que l'utilisateur a tapé dans la recherche depuis qu'il y est. `null` —
  // et non `''` — signifie « rien tapé » : le champ est alors le REFLET de
  // l'adresse enregistrée. Sans cette distinction, il affichait un état
  // purement local, jamais synchronisé avec l'adresse réelle : on pouvait y
  // taper une adresse complète sans jamais choisir de suggestion (champ
  // rempli, rien d'enregistré, bouton gris sans explication — le symptôme même
  // du ticket d'origine), et le vider ne retirait rien (champ vide, adresse
  // toujours tenue pour valide).
  const [typedSearch, setTypedSearch] = useState<string | null>(null);
  const searchText = typedSearch ?? toSearchText(address, i18n.language);

  // De la frappe libre, et toujours rien d'enregistré : c'est EXACTEMENT le
  // symptôme du ticket d'origine — champ rempli, bouton gris, aucune
  // explication. Le parent, lui, ne peut rien dire : il n'allume son message
  // que sur une adresse « touchée », et ici il n'a jamais rien reçu. Seul le
  // composant sait qu'il y a du texte sans suggestion retenue ; c'est donc à
  // lui de le dire.
  //
  // AFFICHAGE DÉRIVÉ, surtout pas un troisième état : les cinq bugs de ce
  // composant venaient tous de transitions entre états.
  const unselectedSearch =
    mode === 'search' &&
    !filled &&
    (typedSearch ?? '').trim().length >= SEARCH_GUIDANCE_MIN_LENGTH;

  // Mémorise si l'adresse portait quelque chose au rendu précédent : la
  // dérivation ci-dessous se déclenche sur la TRANSITION, pas sur l'état
  // courant.
  const prevFilledRef = useRef(filled);

  const addressRef = useRef<HTMLInputElement>(null);

  // `onChange` et `limitFrenchCountry` sont lus dans l'écouteur
  // `place_changed`, qui survit aux rendus : on passe par une ref pour chacun,
  // pour éviter une closure périmée.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const limitFrenchCountryRef = useRef(limitFrenchCountry);
  limitFrenchCountryRef.current = limitFrenchCountry;

  // `goManual` est lui aussi appelé depuis `place_changed` : même ref, et pour
  // une raison plus aiguë encore. L'écouteur ne rejoue pas à chaque frappe, si
  // bien qu'une closure figée y recopierait le `typedSearch` du rendu où il a
  // été branché — c'est-à-dire rien. Affectée plus bas, après la définition ;
  // `null` en attendant, le rendu s'achevant toujours avant que Google ne
  // puisse déclencher quoi que ce soit.
  const goManualRef = useRef<(() => void) | null>(null);

  const status = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${google_api_key}&libraries=places`,
  );

  // `value` peut arriver APRÈS le montage (page profil : le profil est chargé
  // en `useEffect`). L'initialiseur paresseux de `useState` ne rejouant jamais,
  // cette entrée-là est re-dérivée ici.
  useEffect(() => {
    // Réarmement inconditionnel : une ref laissée posée désarmerait la
    // dérivation à vie — le bug d'une itération précédente.
    const wasFilled = prevFilledRef.current;
    prevFilledRef.current = filled;

    // Sur la TRANSITION, pas sur l'état courant : dériver sur l'état
    // contrarierait un « Revenir à la recherche » quand le parent ignore le
    // `onChange` de vidage et redescend l'ancienne adresse.
    if (!wasFilled && filled && mode === 'search') setMode('manual');
    // Pas de transition inverse : voir l'en-tête du fichier. `manual` reste
    // cohérent avec une adresse vidée — quatre champs vides éditables, plus le
    // lien de retour à la recherche : aucun cul-de-sac.
  }, [filled, mode]);

  useEffect(() => {
    if (status !== 'ready' || disabled || mode !== 'search') return;
    if (!addressRef.current || !window.google) return;

    // Google injecte sa liste de suggestions dans `document.body` : on note
    // celles qui existent déjà pour ne retirer que la nôtre au démontage.
    const containersBefore = Array.from(
      document.querySelectorAll('.pac-container'),
    );

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressRef.current,
      {
        componentRestrictions: limitFrenchCountryRef.current
          ? { country: ['fr'] }
          : undefined,
        fields: ['address_components', 'geometry'],
        types: ['address'],
      },
    );

    const listener = autocomplete.addListener('place_changed', () => {
      const parsed = parseGooglePlace(
        autocomplete.getPlace(),
        limitFrenchCountryRef.current,
      );

      // Google peut ne rien renvoyer d'exploitable (validation d'une frappe
      // libre, lieu sans `address_components`, ou place réduite au pays forcé
      // par `limitFrenchCountry`) : on se garde de remonter ça — cela
      // écraserait ce que le parent avait déjà.
      //
      // Dans les deux cas on ouvre les champs : remplis quand la sélection est
      // exploitable, à compléter sinon. Rester sur la recherche laisserait
      // l'utilisateur sans issue.
      if (isUsableAddress(parsed)) {
        onChangeRef.current(parsed);
        setMode('manual');
        return;
      }

      // `place_changed` se déclenche AUSSI quand la frappe est validée par
      // Entrée sans qu'aucune suggestion n'ait été choisie — le geste même du
      // client dont Google ignore l'adresse (STU-28). Poser le mode
      // directement le laissait devant quatre champs vides, alors que le lien
      // de repli, lui, recopie ce qu'il a tapé. On passe donc par `goManual`
      // pour que les deux chemins aient la même sémantique.
      goManualRef.current?.();
    });

    return () => {
      listener.remove();
      // `listener.remove()` ne retire que `place_changed` : l'instance garde
      // ses écouteurs internes sur l'input.
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
      Array.from(document.querySelectorAll('.pac-container'))
        .filter((node) => !containersBefore.includes(node))
        .forEach((node) => node.remove());
    };
    // `limitFrenchCountry` n'est pas en dépendance : il est lu via une ref
    // (ci-dessus), donc l'effet n'a pas besoin de rejouer quand il change. Il
    // ne change pas en cours de vie d'un formulaire de toute façon, mais
    // recréer l'écouteur à chaque rendu serait le prix à payer si un jour ce
    // n'était plus le cas.
  }, [status, disabled, mode]);

  const handleFieldChange = (field: keyof Address, fieldValue: string) => {
    onChange({ ...address, [field]: fieldValue });
  };

  /**
   * Frappe libre dans la recherche. Ce qui y est tapé n'est PAS une adresse :
   * seule une suggestion choisie en produit une. Le champ ne doit donc jamais
   * laisser derrière lui une adresse enregistrée que son contenu ne désigne
   * plus — le vider, en particulier, doit bel et bien la retirer.
   */
  const handleSearchChange = (text: string) => {
    setTypedSearch(text);
    if (filled) onChange({ ...EMPTY_ADDRESS });
  };

  /**
   * SEULE porte d'entrée du mode manuel depuis la recherche, empruntée par le
   * lien « mon adresse n'est pas trouvée » comme par un `place_changed`
   * inexploitable : les deux disent la même chose de l'utilisateur — il a tapé
   * une adresse qu'aucune suggestion ne recouvre — et doivent donc lui faire
   * la même chose.
   */
  const goManual = () => {
    // L'utilisateur vient de taper son adresse sans qu'aucune suggestion ne lui
    // convienne : il la retrouve dans « Rue » plutôt que devant quatre champs
    // vides. On ne remplace jamais une rue déjà connue — le champ de recherche
    // n'en affichait alors que le reflet.
    const typed = (typedSearch ?? '').trim();
    if (typed && isBlank(address.street)) {
      onChange({ ...address, street: typed });
    }
    setMode('manual');
  };
  goManualRef.current = goManual;

  const backToSearch = () => {
    onChange({ ...EMPTY_ADDRESS });
    // `null` et non `''` : le champ redevient le reflet de l'adresse. Avec un
    // parent contrôlé il s'affiche vide, comme l'adresse qu'on vient de vider ;
    // avec un parent qui ignorerait ce vidage, il montre ce que le parent tient
    // encore, plutôt que de faire disparaître des yeux une adresse toujours
    // enregistrée.
    setTypedSearch(null);
    setMode('search');
  };

  // Environ 250 entrées à trier par libellé : on mémoïse sur la langue pour ne
  // pas retrier à chaque frappe dans un autre champ.
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language],
  );

  /**
   * Option à afficher pour le pays enregistré.
   *
   * Un code absent du référentiel ISO — la saisie libre d'avant a laissé un
   * « BELGIE » en base — laissait le champ VIDE alors que la donnée est bien
   * enregistrée, que la règle de complétude la juge présente (donc aucun
   * rouge, aucun blocage) et qu'un enregistrement sans y toucher la conserve.
   * L'écran contredisait la donnée.
   *
   * On synthétise donc une option de repli, dont le libellé est celui que rend
   * déjà `getCountryLabel` dans ce cas : le code brut. `null` reste réservé au
   * pays réellement vide.
   */
  const selectedCountry = useMemo<CountryOption | null>(() => {
    if (isBlank(address.country)) return null;
    return (
      countryOptions.find((option) => option.code === address.country) ?? {
        code: address.country,
        label: getCountryLabel(address.country, i18n.language),
      }
    );
  }, [countryOptions, address.country, i18n.language]);

  // Une option de repli doit AUSSI figurer dans la liste : MUI compare la
  // valeur aux options et tient pour invalide celle qui n'y est pas.
  const countryChoices = useMemo(
    () =>
      selectedCountry && !countryOptions.includes(selectedCountry)
        ? [selectedCountry, ...countryOptions]
        : countryOptions,
    [countryOptions, selectedCountry],
  );

  const editableFields = (readOnly: boolean) => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          id={`${idPrefix}-street`}
          label={t('address.field.street')}
          value={address.street}
          onChange={(e) => handleFieldChange('street', e.target.value)}
          disabled={readOnly}
          error={fieldError('street')}
          InputProps={{ readOnly }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id={`${idPrefix}-postalCode`}
          label={t('address.field.postalCode')}
          value={address.postalCode}
          onChange={(e) => handleFieldChange('postalCode', e.target.value)}
          disabled={readOnly}
          error={fieldError('postalCode')}
          InputProps={{ readOnly }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id={`${idPrefix}-city`}
          label={t('address.field.city')}
          value={address.city}
          onChange={(e) => handleFieldChange('city', e.target.value)}
          disabled={readOnly}
          error={fieldError('city')}
          InputProps={{ readOnly }}
        />
      </Grid>
      {/* Liste fermée, et non un texte libre : la saisie libre a déjà produit
          en base un pays « BELGIE », que la règle du back — France reconnue sur
          `FR`, `FRA` ou `France` — ne sait pas lire. Ce que le repli manuel
          enregistre reste donc l'ISO2 que produit Google ; le libellé traduit
          n'est qu'un affichage. */}
      <Grid item xs={12}>
        <Autocomplete<CountryOption>
          fullWidth
          id={`${idPrefix}-country`}
          options={countryChoices}
          disabled={readOnly}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selected) => option.code === selected.code}
          // `null` pour un pays vide, et pour lui seul : voir `selectedCountry`.
          value={selectedCountry}
          onChange={(_event, selected) =>
            handleFieldChange('country', selected ? selected.code : '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('address.field.country')}
              error={fieldError('country')}
              InputProps={{ ...params.InputProps, readOnly }}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  if (disabled) {
    return editableFields(true);
  }

  if (mode === 'manual') {
    return (
      <div>
        {editableFields(false)}
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="always"
          onClick={backToSearch}
          sx={{ mt: 1 }}
        >
          {t('address.manual.back')}
        </Link>
      </div>
    );
  }

  // mode === 'search'
  return (
    <div>
      <TextField
        fullWidth
        id={`${idPrefix}-search`}
        inputRef={addressRef}
        label={t('address.search.label')}
        value={searchText}
        onChange={(e) => handleSearchChange(e.target.value)}
        autoComplete="off"
        variant="outlined"
        error={error}
        // Le message d'orientation REMPLACE l'invite : les empiler dirait deux
        // fois la même chose. Il n'est pas une erreur de validation —
        // l'utilisateur est en train de saisir, il n'a rien fait de mal — d'où
        // la couleur secondaire imposée, qui tient même si le parent allume
        // `error` par ailleurs (soumission forcée) et repeindrait l'aide en
        // rouge. `error` reste piloté par le parent et garde son sens.
        helperText={
          unselectedSearch ? (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {t('address.search.unselected')}
            </Box>
          ) : (
            t('address.search.hint')
          )
        }
      />
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="always"
        onClick={goManual}
        sx={{ mt: 1 }}
      >
        {t('address.search.notFound')}
      </Link>
    </div>
  );
};

export default AddressInput;
export type { Address };
